import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  CloudArrowUpIcon, 
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Get max file size from environment variable (default 5GB if not set)
const MAX_FILE_SIZE = parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '5368709120', 10);
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export default function FileUpload({ onFileUpload, disabled = false }) {
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);
    
    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Invalid file type. Only video and audio files are allowed.');
      } else {
        setError('File upload failed. Please try again.');
      }
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Validate file type
      const validTypes = [
        // Video types
        'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/quicktime',
        // Audio types
        'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/ogg', 'audio/wma', 'audio/m4a'
      ];
      
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Only video and audio files are allowed.');
        return;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }
      
      // File is valid, call the callback
      onFileUpload(file);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'],
      'audio/*': ['.mp3', '.wav', '.flac', '.aac', '.ogg', '.wma', '.m4a']
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: disabled
  });

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`upload-dropzone ${disabled ? 'disabled' : ''} ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />

        <div className="upload-dropzone-icon">
          <CloudArrowUpIcon />
        </div>

        <div className="upload-dropzone-text">
          <h3>
            {isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}
          </h3>
          <p>or click to browse</p>
        </div>

        <div className="upload-dropzone-meta">
          <span>Supported: MP4, AVI, MOV, MP3, WAV, FLAC, M4A, OGG</span>
          <span className="upload-dropzone-dot">·</span>
          <span>Max size: {MAX_FILE_SIZE_MB} MB</span>
        </div>
      </div>

      {error && (
        <div className="upload-error" role="alert">
          <ExclamationTriangleIcon className="upload-error-icon" />
          <p>{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="upload-error-close"
            aria-label="Dismiss error"
          >
            <XMarkIcon />
          </button>
        </div>
      )}
    </div>
  );
}
